import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Badge,
  HiddenMatchHint,
  ListPage,
  PageHeader,
  RowActions,
  Table,
  TableScroll,
  RowExpandToggle,
  EXPAND_COL_WIDTH,
  ACTIONS_COL_WIDTH,
  IdLink,
} from '@/components/ui';
import ServerPagination from '../../../components/ServerPagination';
import { usePermissions } from '../../../hooks/usePermissions';
import { usePersistedListState } from '../../../hooks/usePersistedListState';
import { ComplianceChecks } from '../../../components/ExpiryIndicator';
import { areasService } from '../../../services/areasService';
import {
  RESOURCE_TYPE_LABELS,
  Vendor,
  VendorQueryParams,
  vendorService,
  type VendorCreatePayload,
} from '../../../services/vendorService';
import VendorsFilters, {
  DEFAULT_VENDOR_FILTERS,
  DEFAULT_VENDOR_STATUSES,
  VendorFilterState,
  countVendorFilters,
  statusFilterHides,
  vendorFiltersDiffer,
} from '../components/VendorsFilters';
import VendorModal from '../components/VendorModal';
import AddVendorToAreaModal from '../components/AddVendorToAreaModal';
import { canOfferAddToMyArea } from '../utils/callerAreas';

/** sessionStorage page key for this page's remembered list state (V2-024). */
const LIST_KEY = 'vendors';

const StatusBadge: React.FC<{ status: Vendor['status'] }> = ({ status }) => {
  if (status === 'ACTIVE') return <Badge bg="success">Active</Badge>;
  if (status === 'PENDING') return <Badge bg="warning">Pending Approval</Badge>;
  return <Badge bg="secondary">Inactive</Badge>;
};

/**
 * HE-M02 — one vendor row that expands to its service areas, and within each area the
 * resources and rates available.
 *
 * Hand-rolls <Table> rather than using DataTable, matching ProjectsView and ProjectsTable:
 * DataTable renders exactly one <tr> per row and has no slot for expanded content, so an
 * expandable list cannot use it. `RowActions` supplies both the expander chevron and the
 * kebab, which is what keeps this consistent with those two despite the hand-rolled table.
 *
 * The cost is that this list scrolls horizontally on a phone instead of collapsing to cards.
 * That matches WorkIds/Projects/Vehicles, which all scroll too - DataTable's card view is the
 * exception among list screens, not the rule.
 *
 * Tiger Calcium, with three service areas, is the worked example the spec names.
 */
const VendorsPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [showModal, setShowModal] = useState(false);

  // Pending (what the controls show) vs applied (what was fetched): nothing refetches until
  // Apply; Clear All resets both to the defaults and applies at once (W-M1). The applied
  // filters, the search and the page are kept for the sign-in (V2-024).
  const [filters, setFilters] = usePersistedListState<VendorFilterState>(LIST_KEY, 'filters', DEFAULT_VENDOR_FILTERS);
  const [pendingFilters, setPendingFilters] = useState<VendorFilterState>(filters);
  const [searchTerm, setSearchTerm] = usePersistedListState(LIST_KEY, 'search', '');
  const [searchInput, setSearchInput] = useState(searchTerm);
  const hasUnappliedChanges = vendorFiltersDiffer(pendingFilters, filters);
  const appliedFilterCount = countVendorFilters(filters);
  const filtersChanged = vendorFiltersDiffer(filters, DEFAULT_VENDOR_FILTERS) || searchTerm !== '';
  const handleApplyFilters = () => setFilters(pendingFilters);
  const handleClearFilters = () => {
    setPendingFilters(DEFAULT_VENDOR_FILTERS);
    setFilters(DEFAULT_VENDOR_FILTERS);
    setSearchInput('');
    setSearchTerm('');
  };
  const handleSearch = (value: string) => {
    setSearchInput(value.trim());
    setSearchTerm(value.trim());
  };
  // The same search with every status, for the hidden-match hint (V2-076, SRCH-05).
  const [unfilteredMatchCount, setUnfilteredMatchCount] = useState<number | null>(null);

  const [savedPage, setSavedPage] = usePersistedListState(LIST_KEY, 'page', { page: 1, limit: 20 });
  const [pagination, setPagination] = useState({ ...savedPage, totalCount: 0, totalPages: 0 });
  useEffect(() => {
    setSavedPage({ page: pagination.page, limit: pagination.limit });
  }, [pagination.page, pagination.limit]);

  const canCreate = hasPermission('heMaster:vendor_create');
  const canAddArea =
    hasPermission('heMaster:vendor_add_area') || hasPermission('heMaster:vendor_edit');
  const canSeeDirectory = hasPermission('heMaster:vendor_directory');

  const [callerAreas, setCallerAreas] = useState<Array<{ id: number; name: string }>>([]);
  const [addTarget, setAddTarget] = useState<Vendor | null>(null);

  useEffect(() => {
    areasService
      .getServiceAreas()
      .then((areas) => setCallerAreas((areas ?? []).map((a) => ({ id: a.id, name: a.name }))))
      .catch(() => setCallerAreas([]));
  }, []);

  const callerAreaIds = callerAreas.map((a) => a.id);

  /** Request filters for the applied filters and search; `withStatus: false` drops the Status filter. */
  const queryFilters = useCallback(
    (withStatus: boolean): VendorQueryParams => ({
      ...(searchTerm && { search: searchTerm }),
      ...(withStatus &&
        filters.statuses.length && {
          statuses: filters.statuses as VendorQueryParams['statuses'],
        }),
      ...(filters.serviceAreaIds.length && { serviceAreaIds: filters.serviceAreaIds }),
      ...(filters.complianceIssuesOnly && { complianceIssuesOnly: true }),
    }),
    [searchTerm, filters]
  );

  const fetchVendors = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        setError(null);
        const params: VendorQueryParams = {
          page,
          limit: pagination.limit,
          ...queryFilters(true),
        };
        const response = await vendorService.getVendorsPaginated(params);
        setVendors(response.data);
        setPagination((prev) => ({
          ...prev,
          page: response.pagination.page,
          totalCount: response.pagination.totalCount,
          totalPages: response.pagination.totalPages,
        }));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load vendors');
        setVendors([]);
        setPagination((prev) => ({ ...prev, totalCount: 0, totalPages: 0 }));
      } finally {
        setLoading(false);
      }
    },
    [pagination.limit, queryFilters]
  );

  // A filter, search or page-size change starts again from page 1; arriving on the page (or
  // coming back to it) restores the remembered page.
  const queryKey = JSON.stringify([searchTerm, filters, pagination.limit]);
  const lastQueryKey = useRef<string | null>(null);
  useEffect(() => {
    const first = lastQueryKey.current === null;
    if (lastQueryKey.current === queryKey) return;
    lastQueryKey.current = queryKey;
    fetchVendors(first ? savedPage.page : 1);
  }, [queryKey]);

  // Hidden-match hint: the same search with every status, one row, reading the total.
  useEffect(() => {
    if (!searchTerm || !statusFilterHides(filters.statuses)) {
      setUnfilteredMatchCount(null);
      return;
    }
    let cancelled = false;
    vendorService
      .getVendorsPaginated({ page: 1, limit: 1, ...queryFilters(false) })
      .then((res) => {
        if (!cancelled) setUnfilteredMatchCount(res.pagination.totalCount);
      })
      .catch(() => {
        if (!cancelled) setUnfilteredMatchCount(null);
      });
    return () => {
      cancelled = true;
    };
  }, [searchTerm, filters, queryFilters]);

  const showAllStatuses = () => {
    const next = { ...filters, statuses: [] };
    setPendingFilters(next);
    setFilters(next);
  };
  const atDefaultStatuses = !vendorFiltersDiffer(
    { ...DEFAULT_VENDOR_FILTERS, statuses: filters.statuses },
    { ...DEFAULT_VENDOR_FILTERS, statuses: DEFAULT_VENDOR_STATUSES }
  );

  const toggleExpanded = (vendorId: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(vendorId)) next.delete(vendorId);
      else next.add(vendorId);
      return next;
    });
  };

  const handleCreate = async (data: VendorCreatePayload) => {
    await vendorService.createVendor(data);
    setShowModal(false);
    await fetchVendors(1);
  };

  return (
    <ListPage className="vendors-page">
      <PageHeader
        title="Vendors"
        actions={[
          ...(canCreate
            ? [
                {
                  key: 'add',
                  label: 'Add Vendor',
                  icon: 'bi-plus-lg',
                  intent: 'primary' as const,
                  onClick: () => setShowModal(true),
                },
              ]
            : []),
        ]}
      />

      {canSeeDirectory && (
        <Alert variant="info" className="tw-mb-3">
          Vendors from every service area are listed here so you can check whether a
          subcontractor is already set up elsewhere before creating a duplicate. You can
          add an existing vendor to your area; you cannot create one outside it.
        </Alert>
      )}

      <VendorsFilters
        filters={pendingFilters}
        onFilterChange={setPendingFilters}
        onClearFilters={handleClearFilters}
        onApply={handleApplyFilters}
        applyDisabled={!hasUnappliedChanges}
        hasActiveFilters={filtersChanged}
        appliedFilterCount={appliedFilterCount}
        search={{
          value: searchInput,
          onChange: setSearchInput,
          onSearch: handleSearch,
          placeholder: 'Name, legal name, email, or phone',
          label: 'Search vendors',
        }}
      />

      {unfilteredMatchCount !== null && !loading && (
        <HiddenMatchHint
          search={searchTerm}
          shownCount={pagination.totalCount}
          unfilteredCount={unfilteredMatchCount}
          shownNoun={atDefaultStatuses ? 'active or pending vendors' : 'vendors with the selected statuses'}
          hiddenNoun={
            atDefaultStatuses
              ? ['inactive match', 'inactive matches']
              : ['match with another status', 'matches with other statuses']
          }
          onShow={showAllStatuses}
        />
      )}

      {error && (
        <div className="tw-mb-3 tw-text-danger" role="alert">
          {error}
        </div>
      )}

      <ListPage.Body>
        <TableScroll>
          <Table hover stickyHeader className="tw-mb-0">
            <thead>
              <tr>
                {/* V2-072 / ACT-04: the expander leads the row in its own column. */}
                <th className="wrap-pin-left" style={{ width: EXPAND_COL_WIDTH }} aria-label="Expand" />
                <th
                  className="wrap-pin-left wrap-pin-edge"
                  style={{ minWidth: '16rem', '--wrap-pin-left': EXPAND_COL_WIDTH } as React.CSSProperties}
                >
                  Vendor
                </th>
                <th>Status</th>
                <th style={{ minWidth: '14rem' }}>Service Areas</th>
                <th>Resources</th>
                <th>Compliance</th>
                <th style={{ width: ACTIONS_COL_WIDTH }} className="wrap-pin-right tw-text-right">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={7} className="tw-text-center tw-py-4">
                    Loading vendors…
                  </td>
                </tr>
              )}

              {!loading && vendors.length === 0 && (
                <tr>
                  <td colSpan={7} className="tw-text-center tw-py-4 tw-text-content-muted">
                    No vendors match these filters.
                  </td>
                </tr>
              )}

              {!loading &&
                vendors.map((vendor) => {
                  const isOpen = expanded.has(vendor.id);
                  return (
                    <React.Fragment key={vendor.id}>
                      {/*
                        Whole row opens the vendor - the same convention DataTable and the
                        assets table use. The kebab keeps its explicit "Open Vendor" action,
                        which is also the keyboard path in, since a <tr> takes no focus.
                      */}
                      <tr
                        onClick={() => navigate(`/vendors/${vendor.id}`)}
                        className="tw-cursor-pointer"
                        title={`Open ${vendor.name}`}
                      >
                        <td className="wrap-pin-left" style={{ width: EXPAND_COL_WIDTH }}>
                          <RowExpandToggle
                            expanded={isOpen}
                            onToggle={() => toggleExpanded(vendor.id)}
                            label={isOpen ? `Hide ${vendor.name}'s service areas` : `Show ${vendor.name}'s service areas`}
                          />
                        </td>
                        <td
                          className="wrap-pin-left wrap-pin-edge"
                          style={{ '--wrap-pin-left': EXPAND_COL_WIDTH } as React.CSSProperties}
                        >
                          {/* V2-072 / ACT-01: the name is the link that opens the vendor. */}
                          <div className="tw-font-medium">
                            <IdLink to={`/vendors/${vendor.id}`} onClick={(e) => e.stopPropagation()}>
                              {vendor.name}
                            </IdLink>
                          </div>
                          {vendor.legalName && (
                            <div className="tw-text-sm tw-text-content-muted">{vendor.legalName}</div>
                          )}
                        </td>
                        <td>
                          <StatusBadge status={vendor.status} />
                        </td>
                        <td>
                          {vendor.serviceAreas?.length
                            ? vendor.serviceAreas
                                .map((sa) => sa.serviceArea.name)
                                .join(', ')
                            : '–'}
                        </td>
                        <td>{vendor._count?.resources ?? 0}</td>
                        <td>
                          <ComplianceChecks compliance={vendor.compliance} />
                        </td>
                        <td className="wrap-pin-right tw-text-right" onClick={(e) => e.stopPropagation()}>
                          {/* V2-072: only the ⋮ menu; the expander leads the row, the name opens it. */}
                          <RowActions
                            actions={[
                              {
                                kind: 'custom',
                                icon: 'bi-geo-alt',
                                label: 'Add to my area',
                                onClick: () => setAddTarget(vendor),
                                hidden: !canAddArea || !canOfferAddToMyArea(vendor, callerAreaIds),
                              },
                            ]}
                          />
                        </td>
                      </tr>

                      {isOpen && (
                        <tr>
                          <td colSpan={7} className="tw-bg-surface-alt tw-p-0">
                            <div className="tw-p-3">
                              {vendor.serviceAreas?.length === 0 && (
                                <p className="tw-text-content-muted tw-mb-0">
                                  This vendor has no service areas.
                                </p>
                              )}
                              {vendor.serviceAreas?.map((link) => {
                                // Resources are only loaded on the detail endpoint; the list
                                // shows the area breakdown and links onward rather than
                                // fetching every vendor's whole catalogue up front.
                                const areaResources = (vendor.resources ?? []).filter((r) =>
                                  r.serviceAreas?.some((sa) => sa.serviceAreaId === link.serviceAreaId)
                                );
                                return (
                                  <div key={link.id} className="tw-mb-3 last:tw-mb-0">
                                    <div className="tw-font-medium tw-mb-1">
                                      <i className="bi bi-geo-alt tw-mr-1" />
                                      {link.serviceArea.name}
                                      {link.serviceArea.code ? ` (${link.serviceArea.code})` : ''}
                                    </div>
                                    {areaResources.length === 0 ? (
                                      <div className="tw-text-sm tw-text-content-muted tw-pl-4">
                                        Open the vendor to see and manage resources and rates in this
                                        area.
                                      </div>
                                    ) : (
                                      <ul className="tw-text-sm tw-pl-5 tw-mb-0">
                                        {areaResources.map((r) => (
                                          <li key={r.id}>
                                            {r.name}{' '}
                                            <span className="tw-text-content-muted">
                                              ({RESOURCE_TYPE_LABELS[r.resourceType]})
                                            </span>
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
            </tbody>
          </Table>
        </TableScroll>
      </ListPage.Body>

      {pagination.totalPages > 1 && (
        <ServerPagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          totalCount={pagination.totalCount}
          pageSize={pagination.limit}
          onPageChange={(page) => fetchVendors(page)}
          onPageSizeChange={(size) => setPagination((prev) => ({ ...prev, limit: size, page: 1 }))}
          loading={loading}
          className="tw-mt-2"
        />
      )}

      <VendorModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onSave={handleCreate}
        callerAreas={callerAreas}
        canAddArea={canAddArea}
        onAdoptVendor={(existing) => {
          setShowModal(false);
          setAddTarget(existing);
        }}
      />

      <AddVendorToAreaModal
        show={!!addTarget}
        vendor={addTarget}
        callerAreas={callerAreas}
        onHide={() => setAddTarget(null)}
        onAdded={async () => {
          setAddTarget(null);
          await fetchVendors(pagination.page);
        }}
      />
    </ListPage>
  );
};

export default VendorsPage;
